import { ArrowDown, Percent, ShoppingBasket, Camera, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { CommunitySection } from "@/components/shared";
import AllocationMath from "@/components/AllocationMath";

const STAGES = [
  {
    icon: Percent,
    step: "01",
    title: "Fees Flow In",
    text: "Every $BASK trade contributes a fixed 4% fee. A share of that fee routes automatically to the treasury collector — no manual intervention, no team discretion.",
    detail: "Fee routing rule • hard-coded • public",
  },
  {
    icon: ShoppingBasket,
    step: "02",
    title: "Basket Purchases Execute",
    text: "Accumulated fees are swapped into the curated basket — SOL, JUP, BONK, WIF and RAY — via Jupiter aggregator routing on a set cadence, weighted to target allocations.",
    detail: "5 assets • target weights 35/25/20/10/10",
  },
  {
    icon: Camera,
    step: "03",
    title: "Eligibility Snapshots",
    text: "At each distribution batch, a snapshot records every wallet's $BASK balance. Wallets above the minimum threshold become eligible for that batch's allocation.",
    detail: "Snapshot per batch • verifiable hash",
  },
  {
    icon: Gift,
    step: "04",
    title: "Proportional Distribution",
    text: "Each eligible wallet is credited a share of the basket proportional to its snapshot balance. Allocations become claimable directly to the holder's wallet — zero lockups.",
    detail: "Your share = your $BASK ÷ eligible supply",
  },
];

const FAQS = [
  {
    q: "Is this a promise of profit?",
    a: "No. The basket mechanism allocates assets acquired from fees — values fluctuate with the market and nothing is guaranteed. This site is a pre-launch prototype with simulated data.",
  },
  {
    q: "Do I need to stake or lock my $BASK?",
    a: "No lockups. Eligibility is based on snapshot balances — simply holding $BASK in your wallet at snapshot time is enough.",
  },
  {
    q: "Who controls the treasury?",
    a: "At launch the vault will be a program-owned address with publicly visible balances and rule-based execution, designed to remove discretionary team custody.",
  },
  {
    q: "Which assets are in the basket?",
    a: "The example basket holds SOL, JUP, BONK, WIF and RAY at 35/25/20/10/10 target weights. Final composition and weights will be published before launch.",
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page" className="bg-[#07050d]">
      <section className="py-20 px-6 border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 sol-glow pointer-events-none opacity-60" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <span className="mono-font text-xs uppercase tracking-widest text-[#9945FF] font-semibold">Protocol Mechanics</span>
          <h1 className="display-font uppercase text-4xl md:text-6xl text-white mt-3 mb-6">
            Fees In. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9945FF] to-[#14F195]">Basket Out.</span>
          </h1>
          <p className="text-base text-[#b5aed0] leading-relaxed">
            Four automated stages turn everyday trading activity into a shared, transparent basket of Solana assets for eligible $BASK holders.
          </p>
        </div>
      </section>

      {/* Pipeline diagram */}
      <section className="py-24 px-6 bg-[#090612] border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          {STAGES.map((s, i) => (
            <div key={s.step} data-testid={`stage-${s.step}`}>
              <div className="glass-panel rounded-2xl p-8 flex gap-6 d1-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-[#9945FF]/15 border border-[#9945FF]/40 flex items-center justify-center shrink-0">
                    <s.icon size={22} className="text-[#14F195]" />
                  </div>
                </div>
                <div>
                  <div className="mono-font text-[10px] text-[#9945FF] uppercase tracking-widest mb-1">Stage {s.step}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-[#b5aed0] leading-relaxed">{s.text}</p>
                  <div className="mt-4 inline-block px-3 py-1 rounded-lg bg-black/40 border border-white/10 text-[11px] mono-font text-[#00F0FF]">
                    {s.detail}
                  </div>
                </div>
              </div>
              {i < STAGES.length - 1 && (
                <div className="flex justify-start pl-[52px] py-2">
                  <ArrowDown size={18} className="text-[#14F195]/60" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ALLOCATION MATH */}
      <AllocationMath />

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#07050d]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Straight Answers</span>
            <h2 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">Common Questions</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-${i + 1}`} className="glass-panel rounded-2xl p-6 hover:border-[#14F195]/40 transition-colors">
                <h3 className="text-base font-bold text-white mb-2">{f.q}</h3>
                <p className="text-sm text-[#b5aed0] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/dashboard" data-testid="hiw-explore-basket-btn"
              className="inline-block px-8 py-4 rounded-xl bg-gradient-to-r from-[#9945FF] to-[#14F195] text-black font-bold text-sm tracking-wider uppercase hover:shadow-[0_0_25px_rgba(20,241,149,0.4)] transition-shadow">
              Explore The Basket
            </Link>
          </div>
        </div>
      </section>

      <CommunitySection />
    </div>
  );
}
